import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from "react-router";
import { message } from 'antd';
import ProductService from 'services/product';
import ProductVariantService from 'services/product_variant';
import ProductOptionService from 'services/product_option';
import ProductTagService from 'services/product_tag';
import ProductCategoryService from 'services/product_category';
import { useApp } from 'contexts/app';
import ExcelJS from 'exceljs';
import _ from "lodash";

const service = new ProductService();
const productVariantService = new ProductVariantService();
const productOptionService = new ProductOptionService();
const productTagService = new ProductTagService();
const productCategoryService = new ProductCategoryService();

const useList = () => {
  const { showConfirm } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [expandedInventoryRows, setExpandedInventoryRows] = useState({});
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
  const [inventoryProduct, setInventoryProduct] = useState(null);
  const [inventoryRows, setInventoryRows] = useState([]);
  const [inventorySaving, setInventorySaving] = useState(false);
  const [inventoryImporting, setInventoryImporting] = useState(false);
  const [inventoryImportPreviewOpen, setInventoryImportPreviewOpen] = useState(false);
  const [inventoryImportPreviewRows, setInventoryImportPreviewRows] = useState([]);
  const [inventoryImportUnmatchedIds, setInventoryImportUnmatchedIds] = useState([]);
  const [inventoryImportSaving, setInventoryImportSaving] = useState(false);
  const [tagNameMap, setTagNameMap] = useState({});
  const [categoryNameMap, setCategoryNameMap] = useState({});
  const [productOptionMap, setProductOptionMap] = useState({});
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [bulkTagModalOpen, setBulkTagModalOpen] = useState(false);
  const [bulkTagIds, setBulkTagIds] = useState([]);
  const [bulkRemoveTagModalOpen, setBulkRemoveTagModalOpen] = useState(false);
  const [bulkRemoveTagIds, setBulkRemoveTagIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkTagSaving, setBulkTagSaving] = useState(false);
  const [bulkRemoveTagSaving, setBulkRemoveTagSaving] = useState(false);

  let [searchParams, setSearchParams] = useSearchParams();

  const getCurrentSearParams = () => {
    const params = JSON.parse(searchParams.get("q")) ?? {};
    if (!params.status) {
      params.status = "all"
    }
    if (!params.stock) {
      params.stock = "all"
    }
    return params;
  }

  var searchPr = getCurrentSearParams();
  const [filter, setFilter] = useState(searchPr)

  useEffect(() => {
    if (!_.isEqual(searchPr, filter)) {
      setFilter(searchPr)
    }
  }, [])

  useEffect(() => {
    delaySearch(filter);
    var searchPr = getCurrentSearParams();
    if (!_.isEqual(searchPr, filter)) {
      setSearchParams({ q: JSON.stringify(filter) })
    }
  }, [filter])

  useEffect(() => {
    loadReferenceData();
  }, [])

  const onSearch = async (data) => {
    var postData = { ...data, info: data?.info };
    if (data?.status == "show") {
      postData.hide = false;
    }
    if (data?.status == "hide") {
      postData.hide = true;
    }
    if (data?.stock == "in_stock") {
      postData.out_of_stock = false;
    }
    if (data?.stock == "out_of_stock") {
      postData.out_of_stock = true;
    }
    var list = await service.list(postData);
    setData(list);
  }

  const delaySearch = useMemo(() => _.debounce(onSearch, 500, { leading: false }), []);

  const setFilterValue = (name, value) => {
    setFilter((prevFilter) => {
      return {
        ...prevFilter,
        [name]: value,
      };
    });
  }

  const loadReferenceData = async () => {
    try {
      const [tagList, categoryList, optionList] = await Promise.all([
        productTagService.list(),
        productCategoryService.list(),
        productOptionService.list(),
      ]);

      const nextTagNameMap = {};
      (tagList || []).forEach(item => {
        if (!item?.id) return;
        nextTagNameMap[String(item.id)] = item?.name || item?.title || "";
      });

      const nextCategoryNameMap = {};
      (categoryList || []).forEach(item => {
        if (!item?.id) return;
        nextCategoryNameMap[String(item.id)] = item?.name || item?.title || "";
      });

      const nextProductOptionMap = {};
      (optionList || []).forEach(option => {
        if (!option?.id) return;
        const valueMap = {};
        (option?.values || []).forEach(v => {
          if (!v?.id) return;
          valueMap[String(v.id)] = v?.title || v?.name || "";
        });
        nextProductOptionMap[String(option.id)] = {
          title: option?.title || option?.name || "",
          valueMap
        };
      });

      setTagNameMap(nextTagNameMap);
      setCategoryNameMap(nextCategoryNameMap);
      setProductOptionMap(nextProductOptionMap);
    } catch (e) {
      console.error(e);
    }
  }

  const getDisplayVariants = (record) => {
    const variants = (record?.variants || []).filter(v => v?.is_active !== false);
    return variants;
  }

  const getSortedVariantsByStock = (record) => {
    return getDisplayVariants(record).slice().sort((a, b) => {
      const stockA = Number(a?.on_hand_quantity ?? 0) || 0;
      const stockB = Number(b?.on_hand_quantity ?? 0) || 0;
      return stockA - stockB;
    });
  }

  const getDisplayText = (item) => {
    if (!item) return "";
    if (typeof item === "string") return item;
    return (
      item?.label ||
      item?.title ||
      item?.name ||
      item?.tag?.name ||
      item?.tag?.title ||
      item?.category?.name ||
      item?.category?.title ||
      item?.product_tag?.name ||
      item?.product_tag?.title ||
      item?.product_category?.name ||
      item?.product_category?.title ||
      ""
    );
  }

  const getItemId = (item) => {
    if (!item && item !== 0) return "";
    if (typeof item === "string" || typeof item === "number") return String(item);
    return String(
      item?.id ||
      item?.value ||
      item?.tag_id ||
      item?.category_id ||
      item?.product_tag_id ||
      item?.product_category_id ||
      item?.tag?.id ||
      item?.category?.id ||
      ""
    );
  }

  const getVariantDisplayName = (variant) => {
    return variant?.variant_title || variant?.title || variant?.name || 'Mặc định';
  }

  const getInventoryImportVariantLabel = (variant) => {
    if (!variant) {
      return 'Không tìm thấy biến thể trong hệ thống';
    }
    const productName = variant?.product?.name || '';
    const variantName = getVariantDisplayName(variant);
    return productName ? `${productName} / ${variantName}` : variantName;
  }

  const getVariantValueDisplay = (variant) => {
    const variantOptions = variant?.options || [];
    if (!Array.isArray(variantOptions) || variantOptions.length === 0) {
      return getVariantDisplayName(variant);
    }

    const parts = variantOptions.map(opt => {
      const optionId = String(opt?.id || "");
      const valueId = String(opt?.value || "");
      if (!optionId || optionId === "default") {
        return "";
      }
      const optionInfo = productOptionMap[optionId];
      const optionTitle = optionInfo?.title || optionId;
      const valueTitle = optionInfo?.valueMap?.[valueId] || valueId;
      return `${optionTitle}: ${valueTitle}`;
    }).filter(Boolean);

    if (!parts.length) {
      return getVariantDisplayName(variant);
    }
    return parts.join(" | ");
  }

  const getProductOptionSummary = (record) => {
    const options = record?.options || [];
    if (!Array.isArray(options) || options.length === 0) {
      return ["Không có biến thể"];
    }

    const displayOptions = options.filter(opt => String(opt?.id || "") !== "default");
    if (!displayOptions.length) {
      return ["Không có biến thể"];
    }

    return displayOptions.map(opt => {
      const optionId = String(opt?.id || "");
      const optionInfo = productOptionMap[optionId];
      const optionTitle = optionInfo?.title || opt?.title || optionId;

      const values = Array.isArray(opt?.values) ? opt.values : [];
      const valueTitles = values.map(v => {
        const valueId = String(v || "");
        if (optionInfo?.valueMap?.[valueId]) {
          return optionInfo.valueMap[valueId];
        }

        const valueFromRecord = (opt?.option_values || []).find(x => String(x?.id || "") === valueId);
        return valueFromRecord?.title || valueId;
      }).filter(Boolean);

      return `${optionTitle}: ${valueTitles.length ? valueTitles.join(", ") : "-"}`;
    });
  }

  const runBulkHideAction = async (hide) => {
    const selectedIds = (selectedProductIds || []).map(String).filter(Boolean);
    if (selectedIds.length === 0) {
      message.warning("Vui lòng chọn sản phẩm");
      return;
    }

    try {
      setBulkActionLoading(true);
      await service.bulkHide(selectedIds, hide);
      message.success(hide ? "Đã ẩn sản phẩm đã chọn" : "Đã hiển thị sản phẩm đã chọn");
      setSelectedProductIds([]);
      await onSearch(filter);
    } catch (e) {
      message.error(e?.message || "Thao tác bulk thất bại");
    } finally {
      setBulkActionLoading(false);
    }
  }

  const openBulkTagModal = () => {
    if (selectedProductIds.length === 0) {
      message.warning("Vui lòng chọn sản phẩm");
      return;
    }
    setBulkTagIds([]);
    setBulkTagModalOpen(true);
  }

  const saveBulkTags = async () => {
    const selectedIds = (selectedProductIds || []).map(String).filter(Boolean);
    if (selectedIds.length === 0) {
      message.warning("Vui lòng chọn sản phẩm");
      setBulkTagModalOpen(false);
      return;
    }

    if (!bulkTagIds.length) {
      message.warning("Vui lòng chọn tag cần gắn");
      return;
    }

    try {
      setBulkTagSaving(true);
      await service.bulkAddTags(selectedIds, _.uniq(bulkTagIds.map(String).filter(Boolean)));
      message.success("Đã gắn tag cho sản phẩm đã chọn");
      setBulkTagModalOpen(false);
      setSelectedProductIds([]);
      await onSearch(filter);
    } catch (e) {
      message.error(e?.message || "Gắn tag hàng loạt thất bại");
    } finally {
      setBulkTagSaving(false);
    }
  }

  const openBulkRemoveTagModal = () => {
    if (selectedProductIds.length === 0) {
      message.warning("Vui lòng chọn sản phẩm");
      return;
    }
    setBulkRemoveTagIds([]);
    setBulkRemoveTagModalOpen(true);
  }

  const saveBulkRemoveTags = async () => {
    const selectedIds = (selectedProductIds || []).map(String).filter(Boolean);
    if (selectedIds.length === 0) {
      message.warning("Vui lòng chọn sản phẩm");
      setBulkRemoveTagModalOpen(false);
      return;
    }

    if (!bulkRemoveTagIds.length) {
      message.warning("Vui lòng chọn tag cần gỡ");
      return;
    }

    try {
      setBulkRemoveTagSaving(true);
      await service.bulkRemoveTags(selectedIds, _.uniq(bulkRemoveTagIds.map(String).filter(Boolean)));
      message.success("Đã gỡ tag cho sản phẩm đã chọn");
      setBulkRemoveTagModalOpen(false);
      setSelectedProductIds([]);
      await onSearch(filter);
    } catch (e) {
      message.error(e?.message || "Gỡ tag hàng loạt thất bại");
    } finally {
      setBulkRemoveTagSaving(false);
    }
  }

  const onBulkActionSelect = ({ key }) => {
    if (key === 'tag') {
      openBulkTagModal();
      return;
    }

    if (key === 'export_inventory') {
      const selectedIds = (selectedProductIds || []).map(String).filter(Boolean);
      if (selectedIds.length === 0) {
        message.warning("Vui lòng chọn sản phẩm");
        return;
      }

      showConfirm({
        title: 'Xuất file kho hàng',
        content: `Bạn có chắc muốn xuất file kho hàng cho ${selectedIds.length} sản phẩm đã chọn?`,
        okText: 'Xuất file',
        cancelText: 'Huỷ',
        onOk: async () => {
          try {
            setBulkActionLoading(true);
            const result = await service.bulkExportInventory(selectedIds);
            if (!result?.file) {
              throw new Error('Không tạo được file excel');
            }
            window.open(result.file, '_blank');
            message.success('Đã tạo file kho hàng');
          } catch (e) {
            message.error(e?.message || 'Xuất file kho hàng thất bại');
          } finally {
            setBulkActionLoading(false);
          }
        }
      });
      return;
    }

    if (key === 'hide') {
      showConfirm({
        title: 'Ẩn sản phẩm trên website',
        content: `Bạn có chắc muốn ẩn ${selectedProductIds.length} sản phẩm đã chọn?`,
        okText: 'Ẩn',
        cancelText: 'Hủy',
        onOk: () => runBulkHideAction(true)
      });
      return;
    }

    if (key === 'remove_tag') {
      openBulkRemoveTagModal();
      return;
    }

    if (key === 'show') {
      showConfirm({
        title: 'Hiển thị sản phẩm trên website',
        content: `Bạn có chắc muốn hiển thị ${selectedProductIds.length} sản phẩm đã chọn?`,
        okText: 'Hiển thị',
        cancelText: 'Hủy',
        onOk: () => runBulkHideAction(false)
      });
    }
  }

  const getProductTags = (record) => {
    const source = record?.tags || record?.product_tags || [];
    if (!Array.isArray(source)) return [];
    return source.map(item => {
      const id = getItemId(item);
      if (id && tagNameMap[id]) {
        return tagNameMap[id];
      }
      return getDisplayText(item);
    }).filter(Boolean);
  }

  const getProductCategories = (record) => {
    const source = record?.categories || record?.category || record?.product_category || [];
    if (Array.isArray(source)) {
      return source.map(item => {
        const id = getItemId(item);
        if (id && categoryNameMap[id]) {
          return categoryNameMap[id];
        }
        return getDisplayText(item);
      }).filter(Boolean);
    }
    const sourceId = getItemId(source);
    const text = (sourceId && categoryNameMap[sourceId]) || getDisplayText(source);
    return text ? [text] : [];
  }

  const openInventoryModal = (record) => {
    const variants = getDisplayVariants(record).map(v => {
      const onHand = Number(v?.on_hand_quantity ?? 0) || 0;
      const reserved = Number(v?.reserved_quantity ?? 0) || 0;
      return {
        ...v,
        next_on_hand_quantity: onHand,
        original_on_hand_quantity: onHand,
        reserved_quantity: reserved,
        available_quantity: Number(v?.available_quantity ?? (onHand - reserved)) || 0,
      }
    });

    setInventoryProduct(record);
    setInventoryRows(variants);
    setInventoryImportPreviewOpen(false);
    setInventoryImportPreviewRows([]);
    setInventoryImportUnmatchedIds([]);
    setInventoryModalOpen(true);
  }

  const onVariantStockChange = (variantId, value) => {
    const nextValue = Math.max(0, Number(value ?? 0) || 0);
    setInventoryRows(prev => prev.map(v => {
      if (v.id !== variantId) {
        return v;
      }
      return {
        ...v,
        next_on_hand_quantity: nextValue,
        available_quantity: nextValue - Number(v?.reserved_quantity ?? 0)
      }
    }))
  }

  const saveInventory = async () => {
    try {
      setInventorySaving(true);
      const changedRows = inventoryRows.filter(v => Number(v.next_on_hand_quantity ?? 0) !== Number(v.original_on_hand_quantity ?? 0));
      if (changedRows.length === 0) {
        message.info("Không có thay đổi tồn kho");
        setInventoryModalOpen(false);
        return;
      }

      await productVariantService.bulkUpdateInventory({
        variants: changedRows.map(v => ({
          id: v.id,
          on_hand_quantity: Number(v.next_on_hand_quantity ?? 0)
        })),
        note: "Quick update from product list"
      });

      message.success("Cập nhật tồn kho thành công");
      setInventoryModalOpen(false);
      await onSearch(filter);
    } catch (e) {
      message.error(e?.message || "Cập nhật tồn kho thất bại");
    } finally {
      setInventorySaving(false);
    }
  }

  const importInventoryFromFile = async (file) => {
    if (!file) {
      return;
    }

    try {
      setInventoryImporting(true);
      const workbook = new ExcelJS.Workbook();
      const buffer = await file.arrayBuffer();
      await workbook.xlsx.load(buffer);
      const sheet = workbook.worksheets[0];
      if (!sheet) {
        message.error('File excel không hợp lệ');
        return;
      }

      const normalizeHeader = (value) => String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      const headerMap = {};
      sheet.getRow(1).eachCell((cell, colNumber) => {
        headerMap[normalizeHeader(cell.value)] = colNumber;
      });

      const variantIdCol = headerMap['variant_id'];
      const onHandCol = headerMap['so luong ton'];
      if (!variantIdCol || !onHandCol) {
        message.error('File mẫu không hợp lệ. Cần có cột variant_id và Số lượng tồn');
        return;
      }

      const importedRows = [];
      for (let rowIndex = 2; rowIndex <= sheet.rowCount; rowIndex++) {
        const row = sheet.getRow(rowIndex);
        const variantID = String(row.getCell(variantIdCol).value || '').trim();
        if (!variantID) {
          continue;
        }
        const rawQty = row.getCell(onHandCol).value;
        const onHandQty = Math.max(0, Number(typeof rawQty === 'object' ? rawQty?.result : rawQty) || 0);
        importedRows.push({
          key: `${variantID}-${rowIndex}`,
          row_index: rowIndex,
          variant_id: variantID,
          next_on_hand_quantity: onHandQty,
        });
      }

      const variantIDs = _.uniq(importedRows.map(row => String(row.variant_id || '').trim()).filter(Boolean));
      const matchedVariants = variantIDs.length > 0
        ? await productVariantService.list({ ids: variantIDs })
        : [];

      const inventoryRowMap = {};
      (matchedVariants || []).forEach(row => {
        inventoryRowMap[String(row.id)] = row;
      });

      const previewRows = importedRows.map(item => {
        const matchedRow = inventoryRowMap[String(item.variant_id)];
        const reservedQuantity = Number(matchedRow?.reserved_quantity ?? 0);
        const currentOnHand = Number(matchedRow?.on_hand_quantity ?? 0);
        return {
          ...item,
          id: matchedRow?.id,
          matched: !!matchedRow,
          variant_title: getInventoryImportVariantLabel(matchedRow),
          current_on_hand_quantity: matchedRow ? currentOnHand : null,
          reserved_quantity: matchedRow ? reservedQuantity : null,
          next_available_quantity: matchedRow ? item.next_on_hand_quantity - reservedQuantity : null
        };
      });

      const matchedCount = previewRows.filter(x => x.matched).length;
      const unmatchedIds = previewRows.filter(x => !x.matched).map(x => x.variant_id);
      if (previewRows.length === 0) {
        message.warning('File không có dữ liệu để import');
      } else if (matchedCount === 0) {
        setInventoryImportPreviewRows(previewRows);
        setInventoryImportUnmatchedIds(unmatchedIds);
        setInventoryImportPreviewOpen(true);
        message.warning('Không có variant_id nào trong file tồn tại trong hệ thống');
      } else {
        setInventoryImportPreviewRows(previewRows);
        setInventoryImportUnmatchedIds(unmatchedIds);
        setInventoryImportPreviewOpen(true);
      }
    } catch (e) {
      message.error(e?.message || 'Nhập tồn kho từ file thất bại');
    } finally {
      setInventoryImporting(false);
    }
  }

  const applyImportedInventory = async () => {
    const matchedRows = inventoryImportPreviewRows.filter(row => row.matched && row.id);
    if (matchedRows.length === 0) {
      message.warning('Không có dữ liệu để cập nhật');
      return;
    }

    try {
      setInventoryImportSaving(true);
      await productVariantService.bulkUpdateInventory({
        variants: matchedRows.map(row => ({
          id: row.id,
          on_hand_quantity: Number(row.next_on_hand_quantity ?? 0)
        })),
        note: 'Import inventory from excel file'
      });

      setInventoryRows(prev => prev.map(row => {
        const imported = matchedRows.find(x => x.id === row.id);
        if (!imported) {
          return row;
        }
        return {
          ...row,
          original_on_hand_quantity: Number(imported.next_on_hand_quantity ?? 0),
          next_on_hand_quantity: Number(imported.next_on_hand_quantity ?? 0),
          available_quantity: Number(imported.next_available_quantity ?? 0)
        };
      }));

      setInventoryImportPreviewOpen(false);
      setInventoryImportPreviewRows([]);
      setInventoryImportUnmatchedIds([]);
      if (inventoryImportUnmatchedIds.length > 0) {
        message.success(`Đã cập nhật ${matchedRows.length} biến thể từ file. Có ${inventoryImportUnmatchedIds.length} dòng có variant_id không tồn tại nên chưa được cập nhật.`);
      } else {
        message.success('Đã cập nhật tồn kho từ file');
      }
      await onSearch(filter);
    } catch (e) {
      message.error(e?.message || 'Cập nhật tồn kho từ file thất bại');
    } finally {
      setInventoryImportSaving(false);
    }
  }

  return {
    navigate,
    data,
    expandedInventoryRows,
    inventoryModalOpen,
    inventoryProduct,
    inventoryRows,
    inventorySaving,
    inventoryImporting,
    inventoryImportPreviewOpen,
    inventoryImportPreviewRows,
    inventoryImportUnmatchedIds,
    inventoryImportSaving,
    tagNameMap,
    categoryNameMap,
    productOptionMap,
    selectedProductIds,
    bulkTagModalOpen,
    bulkTagIds,
    bulkRemoveTagModalOpen,
    bulkRemoveTagIds,
    bulkActionLoading,
    bulkTagSaving,
    bulkRemoveTagSaving,
    filter,
    setFilterValue,
    getDisplayVariants,
    getSortedVariantsByStock,
    getVariantDisplayName,
    getInventoryImportVariantLabel,
    getVariantValueDisplay,
    getProductOptionSummary,
    runBulkHideAction,
    openBulkTagModal,
    saveBulkTags,
    openBulkRemoveTagModal,
    saveBulkRemoveTags,
    onBulkActionSelect,
    getProductTags,
    getProductCategories,
    openInventoryModal,
    onVariantStockChange,
    saveInventory,
    importInventoryFromFile,
    applyImportedInventory,
    setExpandedInventoryRows,
    setInventoryModalOpen,
    setBulkTagIds,
    setBulkTagModalOpen,
    setBulkRemoveTagIds,
    setBulkRemoveTagModalOpen,
    setInventoryImportPreviewOpen,
    setSelectedProductIds,
  };
};

export default useList;
